import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'
import { Layers, MonitorSmartphone, Search, Server, UserCheck, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { leases as leaseApi, users as userApi } from '@/api/endpoints'
import type { LiveVmState } from '@/api/types'
import { Err } from '@/components/Err'
import { Input } from '@/components/ui/input'
import { useSettled } from '@/hooks/usePage'
import { useSession } from '@/hooks/useSession'
import { cn } from '@/lib/utils'
import { findAll } from './search'
import type { Found, Hit, HitKind } from './search'
import type { World } from './model'

// 상단 바의 찾기(F-128). 친 글자는 두 일을 한다 — 지금 화면의 목록을 좁히고(화면마다 `query`), 칸 아래에 **검색 결과**를 연다.
// 검색 결과는 화면별 섹션이고 한 번에 다섯 줄이 보이며 나머지는 스크롤한다. 줄을 누르거나 ↑↓·Enter 로 고르면 그 상세로 가고 찾기는 비워진다 —
// 남겨 두면 돌아간 목록이 그 글자로 좁혀진 채 선다. 열 수 없는 줄(관리자에게 남의 팀 사람)은 흐리게 서고 고를 수 없다.
// 포커스는 칸에 머문다(aria-activedescendant) — 줄을 누르는 순간 칸이 포커스를 잃어 결과가 먼저 닫히지 않게 누름을 막는다.
// **사람과 VM 은 서버에 묻는다**(`useFound`) — 친 말을 모은 뒤, 결과가 열려 있을 때만, 섹션마다 `LIMIT` 줄까지.
// 지금 친 말의 답만 그린다: 늦게 온 앞 말의 답은 버리고, 답을 기다리는 동안 앞 말의 줄을 새 말 아래 세우지 않는다.
// 결과는 열 때마다 새로 묻는다 — 쓰기는 칸을 떠나서 하므로 쓰기 뒤의 다시 읽기에 달지 않는다.

const ICON: Record<HitKind, LucideIcon> = {
  user: UserCheck,
  team: Users,
  vm: MonitorSmartphone,
  cluster: Layers,
  node: Server,
}

/** 사람·VM 섹션이 한 번에 받는 줄 수. */
const LIMIT = 20
/** 산 게스트의 상태 — 지워졌거나 오류인 게스트의 임대는 찾지 않는다. */
const LIVE_VM: LiveVmState[] = ['planned', 'provisioning', 'running', 'stopped', 'destroying']

/** 친 말 하나의 답. */
type Answer = { term: string; found: Found | null; error: Error | null }

/** 사람과 VM 을 서버에 묻는다. `waiting` 은 지금 친 말의 답이 아직 오지 않은 때다. */
function useFound(query: string, open: boolean): { found: Found | null; error: Error | null; waiting: boolean } {
  const term = query.trim()
  const settled = useSettled(term)
  const [answer, setAnswer] = useState<Answer | null>(null)
  useEffect(() => {
    // 모으는 중인 말은 묻지 않는다 — 다시 연 칸이 앞서 모은 옛 말을 묻지 않게.
    if (!open || term === '' || settled !== term) return
    let alive = true
    Promise.all([
      userApi.page({ q: [term], withdrawn: false, limit: LIMIT, offset: 0 }),
      leaseApi.page({ q: [term], vm_state: LIVE_VM, limit: LIMIT, offset: 0 }),
    ]).then(
      ([users, leases]) => {
        if (alive) setAnswer({ term, found: { users, leases }, error: null })
      },
      (e: unknown) => {
        if (alive) setAnswer({ term, found: null, error: e instanceof Error ? e : new Error(String(e)) })
      },
    )
    return () => {
      alive = false
    }
  }, [open, term, settled])
  const now = answer?.term === term ? answer : null
  return { found: now?.found ?? null, error: now?.error ?? null, waiting: term !== '' && now === null }
}

export function TopSearch({ world, query, onQuery }: { world: World; query: string; onQuery: (v: string) => void }) {
  const me = useSession()
  const nav = useNavigate()
  const id = useId()
  const box = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)
  // Esc 로 닫은 결과는 다시 치거나 ↓ 를 누를 때까지 닫혀 있다.
  const [shut, setShut] = useState(false)
  const [active, setActive] = useState(-1)
  const open = focused && !shut && query.trim() !== ''
  const { found, error, waiting } = useFound(query, open)
  const groups = open ? findAll(world, me, query, found) : []
  const hits = groups.flatMap((g) => g.hits)
  // 섹션마다 첫 줄의 번호 — 줄 번호는 섹션을 가로질러 하나로 센다.
  const from = groups.map((_, k) => groups.slice(0, k).reduce((sum, g) => sum + g.hits.length, 0))
  const usable = (i: number) => hits[i]?.to != null
  // 고른 줄이 사라지거나 열 수 없으면 첫 줄이 고른 줄이다 — Enter 가 늘 무엇을 여는지 보인다.
  const current = usable(active) ? active : hits.findIndex((h) => h.to !== null)
  const optionId = (i: number) => `${id}-hit-${i}`
  const listId = `${id}-hits`

  const go = (h: Hit) => {
    if (h.to === null) return
    onQuery(h.query ?? '')
    box.current?.blur()
    nav(h.to)
  }
  const move = (step: 1 | -1) => {
    for (let i = current + step; i >= 0 && i < hits.length; i += step) {
      if (!usable(i)) continue
      setActive(i)
      document.getElementById(optionId(i))?.scrollIntoView?.({ block: 'nearest' })
      return
    }
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) setShut(false)
      else move(e.key === 'ArrowDown' ? 1 : -1)
    } else if (e.key === 'Enter' && open && current >= 0) {
      e.preventDefault()
      go(hits[current]!)
    } else if (e.key === 'Escape') {
      setShut(true)
    }
  }

  return (
    <>
      <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={box}
        value={query}
        onChange={(e) => {
          onQuery(e.target.value)
          setShut(false)
          setActive(-1)
        }}
        onFocus={() => {
          setFocused(true)
          setShut(false)
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={onKey}
        role="combobox"
        aria-label="검색"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && current >= 0 ? optionId(current) : undefined}
        autoComplete="off"
        className="h-9 rounded-full bg-background pl-9 text-[13px]"
      />
      {open ? (
        <div className="absolute top-full left-0 z-50 mt-1.5 w-104 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          {error ? (
            <div className="px-2 pt-2">
              <Err error={error} />
            </div>
          ) : null}
          {/* 다섯 줄 — 섹션 머리 하나와 줄 다섯이 한 화면이고 나머지는 스크롤한다. 머리는 스크롤해도 위에 붙어 있다. */}
          <div id={listId} role="listbox" aria-label="검색 결과" className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {hits.length === 0 && error === null ? (
              <div className="px-3 py-2.5 text-[13px] text-muted-foreground">{waiting ? '불러오는 중' : '검색 결과 없음'}</div>
            ) : null}
            {groups.map((g, k) => {
              const Icon = ICON[g.kind]
              return (
                <div key={g.kind} role="group" aria-label={g.title}>
                  <div aria-hidden className="sticky top-0 z-10 flex h-7 items-center gap-1.5 bg-popover px-3 text-[11px] font-medium text-muted-foreground">
                    <Icon className="size-3.5" />
                    {g.title}
                    {/* 서버가 찾은 섹션은 맞은 수가 받은 줄보다 많으면 둘 다 말한다 — 받은 수만 말하면 맞은 수로 읽힌다. */}
                    <span className="tabular-nums">{g.total !== undefined && g.total > g.hits.length ? `${g.hits.length}/${g.total}` : g.hits.length}</span>
                  </div>
                  {g.hits.map((h, j) => {
                    const i = from[k]! + j
                    return (
                      <div
                        key={h.key}
                        id={optionId(i)}
                        role="option"
                        aria-selected={i === current}
                        aria-disabled={h.to === null || undefined}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseMove={() => (usable(i) && i !== active ? setActive(i) : undefined)}
                        onClick={() => go(h)}
                        className={cn(
                          'mx-1 flex h-11 scroll-mt-7 flex-col justify-center rounded-sm px-2',
                          i === current && 'bg-accent text-accent-foreground',
                          h.to === null ? 'opacity-50' : 'cursor-pointer',
                        )}
                      >
                        <span className="truncate text-[13px] font-medium">
                          <Marked text={h.label} query={query} />
                        </span>
                        {h.detail ? (
                          <span className="truncate text-[11px] text-muted-foreground">
                            <Marked text={h.detail} query={query} />
                          </span>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </>
  )
}

/** 친 글자가 걸린 자리를 굵게 — 이름이 아니라 소속·직위로 걸린 줄은 보조 줄이 왜 걸렸는지 말한다. */
function Marked({ text, query }: { text: string; query: string }) {
  const q = query.trim().toLowerCase()
  const at = q === '' ? -1 : text.toLowerCase().indexOf(q)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <b className="font-semibold text-brand">{text.slice(at, at + q.length)}</b>
      {text.slice(at + q.length)}
    </>
  )
}
